/* global React, Icon, useState, useEffect */
// ============================================================
// PLUMME — contextual help. The nearest data-help target owns the explanation.
// ============================================================

// Shared toggle state; the shell owns the single help layer.
let _helpOn = false;
function helpIsOn() { return _helpOn; }
function helpSet(v) { _helpOn = v; window.dispatchEvent(new CustomEvent('plumme-help-toggle', { detail: v })); }

// Explicit escape controls remain usable while help intercepts other actions.
function HelpButton({ className }) {
  const [on, setOn] = useState(helpIsOn);
  useEffect(() => {
    const h = (e) => setOn(e.detail);
    window.addEventListener('plumme-help-toggle', h);
    return () => window.removeEventListener('plumme-help-toggle', h);
  }, []);
  return <button className={'helpmode-btn' + (on ? ' on' : '') + (className ? ' ' + className : '')}
    type="button" data-help-control onClick={() => helpSet(!on)} title={t('hlp.explainMode')} aria-label={t('hlp.explainMode')}
    aria-pressed={on}>
    <Icon name="help" size={18} />
  </button>;
}

// Banner, target highlight and a viewport-constrained explanation.
function HelpLayer() {
  const [on, setOn] = useState(helpIsOn);
  const [tip, setTip] = useState(null);
  const tipRef = React.useRef(null);

  React.useLayoutEffect(() => {
    if (!tip || !tipRef.current) return;
    const bubble = tipRef.current;
    const anchor = tip.element.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const topMargin = Math.max(14, (document.querySelector('.help-banner')?.getBoundingClientRect().bottom || 0) + 12);
    bubble.style.maxHeight = Math.max(60, window.innerHeight - topMargin - 14) + 'px';
    const width = bubble.offsetWidth, height = bubble.offsetHeight, margin = 14;
    const x = Math.max(margin, Math.min(anchor.left + anchor.width / 2 - width / 2, viewportWidth - width - margin));
    const below = anchor.bottom + 12 + height <= window.innerHeight - margin || anchor.top < height + margin + 12;
    const y = Math.max(topMargin, Math.min(below ? anchor.bottom + 12 : anchor.top - height - 12, window.innerHeight - height - margin));
    bubble.style.left = x + 'px'; bubble.style.top = y + 'px';
    bubble.className = 'help-tip help-tip-' + (below ? 'below' : 'above');
  }, [tip]);

  useEffect(() => {
    const h = (e) => { setOn(e.detail); if (!e.detail) setTip(null); };
    window.addEventListener('plumme-help-toggle', h);
    return () => window.removeEventListener('plumme-help-toggle', h);
  }, []);

  // Keyboard shortcuts: '?' (when not in input) and Ctrl+I
  useEffect(() => {
    const onKey = (e) => {
      const active = document.activeElement;
      const inInput = active?.isContentEditable || ['INPUT','TEXTAREA','SELECT'].includes(active?.tagName);
      const isQuestion = e.key === '?' && !e.ctrlKey && !e.metaKey && !inInput;
      const isCtrlI = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'i' && !e.shiftKey && !inInput;
      if (isQuestion || isCtrlI) { e.preventDefault(); helpSet(!helpIsOn()); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('help-mode', on);
    if (!on) return undefined;

    const onMove = (e) => {
      const t = e.target.closest?.('[data-help]');
      document.querySelectorAll('.help-hot').forEach((el) => { if (el !== t) el.classList.remove('help-hot'); });
      if (t) t.classList.add('help-hot');
    };
    const onClick = (e) => {
      if (e.target.closest?.('[data-help-control], .help-tip')) return;
      const target = e.target.closest?.('[data-help]');
      const interactive = e.target.closest?.('button, a, input, select, textarea, [role="button"], [role="switch"], [role="checkbox"]');
      if (!target && !interactive) { setTip(null); return; }
      e.preventDefault(); e.stopPropagation();
      setTip({
        element: target || interactive,
        title: target?.getAttribute('data-help-title') || t('hlp.explainMode'),
        body: target?.getAttribute('data-help') || t('helpTopics.unavailable.body'),
      });
    };
    const dismiss = (event) => { if (!tipRef.current?.contains(event.target)) setTip(null); };
    const onInspectStart = (e) => {
      if (e.target.closest?.('[data-help-control], .help-tip')) return;
      if (e.target.closest?.('[disabled][data-help], [data-help] [disabled]')) { onClick(e); return; }
      if (e.target.closest?.('[data-help], button, a, input, select, textarea, [role="button"], [role="switch"], [role="checkbox"]')) { e.preventDefault(); e.stopPropagation(); }
    };
    const onKey = (e) => { if (e.key === 'Escape') helpSet(false); else if (e.key === 'Enter' || e.key === ' ') onClick(e); };
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onInspectStart, true);
    document.addEventListener('mousedown', onInspectStart, true);
    window.addEventListener('resize', dismiss);
    window.addEventListener('scroll', dismiss, true);
    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onInspectStart, true);
      document.removeEventListener('mousedown', onInspectStart, true);
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('scroll', dismiss, true);
      document.body.classList.remove('help-mode');
      document.querySelectorAll('.help-hot').forEach((el) => el.classList.remove('help-hot'));
    };
  }, [on]);

  if (!on) return null;
  return <React.Fragment>
    <div className="help-banner">
      <span className="help-banner-ic"><Icon name="help" size={17} /></span>
      <span><b>{t('hlp.helpModeOn')}</b> {t('hlp.tapWhatever')} <span className="help-banner-hint" style={{opacity:0.7,fontSize:'0.9em'}}>{t('hlp.shortcutHint')}</span></span>
      <button type="button" data-help-control className="help-banner-x" onClick={() => helpSet(false)}>{t('hlp.exit')}</button>
    </div>
    {tip && <div ref={tipRef} className="help-tip" role="status" aria-live="polite">
      <div className="help-tip-title">{tip.title}</div>
      <div className="help-tip-body">{tip.body}</div>
    </div>}
  </React.Fragment>;
}

Object.assign(window, { HelpButton, HelpLayer, helpSet });
